import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { contractService, paymentSchedulerService, paymentService } from '../../../services/api'
import { useAuth } from '../../../context/EcoAppsAuthContext'

const PaymentScheduler = () => {
  const { user } = useAuth()
  const isLandlord = user?.role === 'landlord'
  const currentYear = new Date().getFullYear()
  
  // Contracts state
  const [contracts, setContracts] = useState([])
  const [selectedContract, setSelectedContract] = useState(null)
  const [loadingContracts, setLoadingContracts] = useState(true)
  
  // Schedule status state
  const [scheduleStatus, setScheduleStatus] = useState(null)
  const [loadingStatus, setLoadingStatus] = useState(false)
  
  // Rent scheduling state
  const [rentYear, setRentYear] = useState(currentYear)
  const [rentPaymentDay, setRentPaymentDay] = useState(1)
  const [rentPreview, setRentPreview] = useState(null)
  const [rentLoading, setRentLoading] = useState(false)
  
  // Utility scheduling state
  const [utilityType, setUtilityType] = useState('electricity')
  const [utilityYear, setUtilityYear] = useState(currentYear)
  const [utilityPaymentDay, setUtilityPaymentDay] = useState(1)
  const [utilityAmount, setUtilityAmount] = useState('')
  
  // Reschedule modal state
  const [rescheduleLoading, setRescheduleLoading] = useState(false)
  
  // Filter state for schedule status table
  const [statusFilter, setStatusFilter] = useState('all') // all, rent, electricity, water, gas
  const [statusYear, setStatusYear] = useState(currentYear)
  
  // General state
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [activeTab, setActiveTab] = useState('rent')

  useEffect(() => {
    fetchContracts()
  }, [])

  useEffect(() => {
    if (selectedContract) {
      fetchScheduleStatus(statusYear)
    }
  }, [selectedContract, statusYear])

  const fetchContracts = async () => {
    try {
      setLoadingContracts(true)
      const response = await contractService.getAll({ status: 'active', limit: 100 })
      setContracts(response.data.data.contracts)
    } catch (error) {
      console.error('Error fetching contracts:', error)
      setError('Error al cargar los contratos')
    } finally {
      setLoadingContracts(false)
    }
  }

  const fetchScheduleStatus = async (yearParam = statusYear) => {
    try {
      setLoadingStatus(true)
      const response = await paymentSchedulerService.getScheduleStatus(selectedContract.id, yearParam)
      setScheduleStatus(response.data.data)
      
      // Set default payment day from contract
      if (response.data.data.payment_day) {
        setRentPaymentDay(response.data.data.payment_day)
      }
    } catch (error) {
      console.error('Error fetching schedule status:', error)
    } finally {
      setLoadingStatus(false)
    }
  }

  const handleContractSelect = (contract) => {
    setSelectedContract(contract)
    setStatusYear(activeTab === 'utility' ? utilityYear : rentYear)
    setUtilityAmount('')
    setSuccess(null)
    setError(null)
  }

  const previewRentSchedule = async () => {
    if (!selectedContract) return
    
    console.log('previewRentSchedule called with rentYear:', rentYear)
    
    try {
      const response = await paymentSchedulerService.preview({
        contract_id: selectedContract.id,
        type: 'rent',
        year: rentYear,
        payment_day: rentPaymentDay
      })
      console.log('Rent preview response:', response.data.data)
      setRentPreview(response.data.data)
      setError(null)
    } catch (error) {
      console.error('Error previewing rent schedule:', error)
      setError(error.response?.data?.message || 'Error al previsualizar programacion')
    }
  }

  const scheduleRentPayments = async () => {
    if (!selectedContract) return
    
    try {
      setRentLoading(true)
      const response = await paymentSchedulerService.scheduleRent({
        contract_id: selectedContract.id,
        year: rentYear,
        payment_day: rentPaymentDay
      })
      setSuccess(`Pagos de renta programados: ${response.data.data.scheduled} creados, ${response.data.data.skipped} omitidos`)
      setRentPreview(null)
      setStatusYear(rentYear)
      fetchScheduleStatus(rentYear)
      setError(null)
    } catch (error) {
      console.error('Error scheduling rent payments:', error)
      setError(error.response?.data?.message || 'Error al programar pagos de renta')
    } finally {
      setRentLoading(false)
    }
  }

  const rescheduleUtilityPayments = async () => {
    if (!selectedContract) return
    
    // Get existing amount if not provided
    let amount = parseFloat(utilityAmount)
    if (!utilityAmount || isNaN(amount)) {
      // Try to get amount from existing payments
      const existingPayments = scheduleStatus?.types[utilityType]?.payments || []
      if (existingPayments.length > 0 && existingPayments[0].amount) {
        amount = parseFloat(existingPayments[0].amount)
      } else {
        setError('Por favor ingresa un monto para el servicio')
        return
      }
    }
    
    const existingCount = scheduleStatus?.types[utilityType]?.existing || 0
    const confirmMessage = existingCount > 0 
      ? `Estas seguro de reprogramar todos los pagos de ${utilityType} para ${utilityYear}? Los pagos que esten marcados como "pagados" se preservaran. Los demas se eliminaran y crearan nuevos con las fechas correctas de mes vencido.`
      : `Estas seguro de programar todos los pagos de ${utilityType} para ${utilityYear}? Se crearan 12 pagos con las fechas correctas de mes vencido.`
    if (!window.confirm(confirmMessage)) {
      return
    }
    
    try {
      setRescheduleLoading(true)
      const response = await paymentSchedulerService.rescheduleUtility({
        contract_id: selectedContract.id,
        utility_type: utilityType,
        year: utilityYear,
        payment_day: utilityPaymentDay,
        amount: amount
      })
      const copies = response.data.data.copies || 0
      setSuccess(`Pagos de ${utilityType} reprogramados: ${copies} copias de respaldo, ${response.data.data.deleted} eliminados, ${response.data.data.created} nuevos`)
      setStatusYear(utilityYear)
      fetchScheduleStatus(utilityYear)
      setError(null)
    } catch (error) {
      console.error('Error rescheduling utility payments:', error)
      setError(error.response?.data?.message || 'Error al reprogramar pagos de servicios')
    } finally {
      setRescheduleLoading(false)
    }
  }

  // Inline update payment amount
  const updatePaymentAmount = async (paymentId, newAmount) => {
    try {
      await paymentService.update(paymentId, { amount: newAmount })
      fetchScheduleStatus(statusYear)
      setSuccess('Monto actualizado correctamente')
    } catch (error) {
      console.error('Error updating payment amount:', error)
      setError('Error al actualizar el monto')
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    // Fix timezone issue by appending time to force local timezone parsing
    const dateStrFixed = dateStr.includes('T') ? dateStr : `${dateStr}T12:00:00`
    const date = new Date(dateStrFixed)
    if (isNaN(date.getTime())) return 'Invalid Date'
    return date.toLocaleDateString('es-MX', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0)
  }

  const getMonthName = (dueDate) => {
    // Handle both "YYYY-MM-DD" and "YYYY-MM" formats
    
    // Fix timezone issue: append time to force local timezone parsing
    const dateStr = dueDate.includes('T') ? dueDate : `${dueDate}T12:00:00`
    const date = new Date(dateStr)
    
    // Get month name using local date parts
    const monthName = date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })
    return monthName
  }

  // Get service period for display
  const getServicePeriod = (dueDate, paymentType) => {
    const dateStr = dueDate.includes('T') ? dueDate : `${dueDate}T12:00:00`
    const date = new Date(dateStr)
    
    if (paymentType === 'rent') {
      // Rent: period is the SAME month as due_date
      // due_date 2026-01-05 → Rent for January 2026
      return date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })
    }
    // Services (mes vencido): period is the SAME month as due date
    // due_date 2026-01-05 → Service period January 2026
    return date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })
  }

  const getMissingMonthsDisplay = (missingMonths) => {
    if (!missingMonths || missingMonths.length === 0) return ''
    return missingMonths.map(m => {
      const [year, month] = m.split('-')
      const date = new Date(parseInt(year), parseInt(month) - 1, 1)
      return date.toLocaleDateString('es-MX', { month: 'long' })
    }).join(', ')
  }

  const utilityTypes = [
    { value: 'electricity', label: 'Electricidad' },
    { value: 'water', label: 'Agua' },
    { value: 'gas', label: 'Gas' }
  ]

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i)

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Programacion de Pagos</h1>
          <p className="text-gray-500">Programa pagos automaticos de renta y servicios publicos</p>
        </div>
        <Link to="/payments" className="btn btn-secondary">
          Ver Todos los Pagos
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Contract Selection */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Seleccionar Contrato</h2>
        {loadingContracts ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : contracts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No hay contratos activos disponibles
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contracts.map((contract) => (
              <div
                key={contract.id}
                onClick={() => handleContractSelect(contract)}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedContract?.id === contract.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-300'
                }`}
              >
                <div className="font-medium text-gray-900">{contract.contract_number}</div>
                <div className="text-sm text-gray-500 mt-1">
                  {contract.property?.name || 'Sin propiedad'}
                </div>
                <div className="text-sm text-gray-500">
                  Inicio: {formatDate(contract.start_date)}
                </div>
                <div className="text-sm font-medium text-primary-600 mt-2">
                  Renta: {formatCurrency(contract.monthly_rent)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedContract && (
        <>
          {/* Schedule Status */}
          {loadingStatus ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : scheduleStatus && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <h2 className="text-lg font-semibold text-gray-900">Estado de Programacion - {scheduleStatus.year}</h2>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">Todos</option>
                    <option value="rent">Renta</option>
                    <option value="electricity">Electricidad</option>
                    <option value="water">Agua</option>
                    <option value="gas">Gas</option>
                  </select>
                </div>
                <button
                  onClick={() => fetchScheduleStatus()}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Refrescar
                </button>
              </div>
              
              {/* Payments tables with editable amounts */}
              {Object.entries(scheduleStatus.types)
                .filter(([type]) => statusFilter === 'all' || type === statusFilter)
                .map(([type, typeData]) => (
                  <div key={type} className="mb-6">
                    <h3 className="text-md font-semibold text-gray-900 capitalize mb-2">{type}</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mes</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha Vencimiento</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {typeData.payments.map((payment, idx) => (
                            <tr key={`${type}-${idx}`} className="hover:bg-gray-50">
                              <td className="px-4 py-2 text-sm text-gray-900">{getServicePeriod(payment.due_date, type)}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{formatDate(payment.due_date)}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                <input
                                  type="number"
                                  defaultValue={payment.amount}
                                  onBlur={(e) => {
                                    if (e.target.value !== String(payment.amount)) {
                                      updatePaymentAmount(payment.id, e.target.value)
                                    }
                                  }}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </td>
                              <td className="px-4 py-2 text-sm">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  payment.status === 'paid' ? 'bg-green-100 text-green-800' :
                                  payment.status === 'overdue' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {payment.status}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm">
                                <button
                                  onClick={() => {
                                    setActiveTab('utility')
                                    setUtilityType(type)
                                  }}
                                  className="text-xs text-orange-600 hover:text-orange-800 underline"
                                >
                                  Reprogramar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              
              {/* Summary */}
              <div className="mt-4 grid grid-cols-4 gap-4 text-sm">
                {Object.entries(scheduleStatus.types)
                  .filter(([type]) => statusFilter === 'all' || type === statusFilter)
                  .map(([type, data]) => (
                    <div key={type} className="p-2 bg-gray-50 rounded">
                      <div className="font-medium capitalize">{type}</div>
                      <div className="text-gray-500">{data.existing}/{data.expected} programados</div>
                      {data.missing > 0 && (
                        <div className="text-orange-600 font-medium">
                          {data.missing} faltantes: {getMissingMonthsDisplay(data.missingMonths)}
                        </div>
                      )}
                      {data.missing > 0 && (
                        <button
                          onClick={() => {
                            setActiveTab(type === 'rent' ? 'rent' : 'utility');
                            if (type !== 'rent') {
                              setUtilityType(type);
                            }
                          }}
                          className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          Generar faltantes
                        </button>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => {
                  setActiveTab('rent')
                  setStatusYear(rentYear)
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'rent'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Pagos de Renta
              </button>
              <button
                onClick={() => {
                  setActiveTab('utility')
                  setStatusYear(utilityYear)
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'utility'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Servicios Publicos
              </button>
            </nav>
          </div>

          {/* Rent Scheduling */}
          {activeTab === 'rent' && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Programar Pagos de Renta</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ano</label>
                  <select
                    value={rentYear}
                    onChange={(e) => {
                      const year = parseInt(e.target.value)
                      setRentYear(year)
                      setStatusYear(year)
                      setRentPreview(null)
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dia de Pago</label>
                  <select
                    value={rentPaymentDay}
                    onChange={(e) => setRentPaymentDay(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <option key={day} value={day}>{day} de cada mes</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={previewRentSchedule}
                    className="btn btn-secondary flex-1"
                  >
                    Previsualizar
                  </button>
                </div>
              </div>

              {rentPreview && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-900">
                        Se crearan {rentPreview.will_create} pagos ({rentPreview.will_skip} omitidos)
                      </span>
                      <button
                        onClick={scheduleRentPayments}
                        disabled={rentLoading || rentPreview.will_create === 0}
                        className="btn btn-primary"
                      >
                        {rentLoading ? 'Programando...' : 'Confirmar Programacion'}
                      </button>
                    </div>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mes</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha Vencimiento</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {rentPreview.payments_to_create.map((preview, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 text-sm text-gray-900">{preview.period}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatDate(preview.due_date)}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(preview.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Utility Scheduling */}
          {activeTab === 'utility' && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Programar Servicios Publicos</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Servicio</label>
                  <select
                    value={utilityType}
                    onChange={(e) => setUtilityType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {utilityTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ano</label>
                  <select
                    value={utilityYear}
                    onChange={(e) => {
                      const year = parseInt(e.target.value)
                      setUtilityYear(year)
                      setStatusYear(year)
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dia de Pago</label>
                  <select
                    value={utilityPaymentDay}
                    onChange={(e) => setUtilityPaymentDay(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <option key={day} value={day}>{day} de cada mes</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monto Mensual</label>
                  <input
                    type="number"
                    value={utilityAmount}
                    onChange={(e) => setUtilityAmount(e.target.value)}
                    placeholder={scheduleStatus?.types[utilityType]?.payments?.[0]?.amount || ''}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={rescheduleUtilityPayments}
                  disabled={rescheduleLoading}
                  className="btn btn-primary"
                >
                  {rescheduleLoading ? 'Reprogramando...' : 'Reprogramar Pagos'}
                </button>
              </div>
              
              {scheduleStatus?.types[utilityType] && (
                <div className="mt-4 text-sm text-gray-500">
                  {scheduleStatus.types[utilityType].existing > 0 
                    ? `${scheduleStatus.types[utilityType].existing} pagos existentes. Se preservaran los marcados como "pagados".`
                    : 'No hay pagos existentes para este servicio'}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default PaymentScheduler
